'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import Image from 'next/image'

import Title from '@/components/content/common/Title'
import Container from '@/components/structure/Container'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import AppIcon from '@/components/content/app/AppIcon'

import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { getBrandColors } from '@/lib/utils'
import { App } from '@/payload-types'
import { easeMedium } from '@/lib/transitions'

type AboutMissionProps = {
  apps: Pick<
    App,
    | 'id'
    | 'name'
    | 'slug'
    | 'icon'
    | 'glyph'
    | 'brandBackgroundColor'
    | 'brandPrimaryColor'
    | 'brandSecondaryColor'
    | 'brandTertiaryColor'
  >[]
}

type Editor = {
  id: number
  name: string
  role: string
  offset: number
}

const editors: Editor[] = [
  {
    id: 0,
    name: 'Alex Bass',
    role: 'Founder & CEO',
    offset: 0,
  },
  {
    id: 1,
    name: 'Andra Vomir',
    role: 'Co-Founder',
    offset: -6,
  },
]

type CollectionProps = {
  name: string
  icon: string
  text: string
}

const collections: CollectionProps[] = [
  {
    name: 'App reviews',
    icon: 'review',
    text: `We don't fall for the marketing hype. Every review comes from real testing and deep evaluation, so we only recommend what truly works.`,
  },
  {
    name: 'Deals',
    icon: 'deal',
    text: `We negotiate the best deals with software providers. The more you use Efficient App to sign up for tools, the more exclusive offers you're eligible for.`,
  },
  {
    name: 'Stacks',
    icon: 'stack',
    text: `“Just tell me what to use!” Stacks are curated lists of apps that work well together. Perfect for those who want a shortcut and trust our recommendations—no deep dives required.`,
  },
  {
    name: 'Compare',
    icon: 'comparison',
    text: `Deciding between multiple apps? Compare up to four apps at a time to find which app is best for you. No second-guessing.`,
  },
  {
    name: 'Alternatives',
    icon: 'alternative',
    text: `Switching apps? Alternatives give you curated lists of the best options. But sometimes, the grass may not be greener—we’ll let you know if that's the case.`,
  },
  {
    name: 'Efficient awards',
    icon: 'best',
    text: `Efficient Awards are given only to the best apps in each category. Apps can’t apply, and they can’t pay for placement. Awards are reserved for truly exceptional products.`,
  },
]

export default function AboutMission({ apps }: AboutMissionProps) {
  const [selected, setSelected] = useState(0)

  return (
    <div>
      <Container className="mx-auto flex w-fit flex-col gap-12 md:flex-row lg:gap-24 xl:gap-44">
        <div className="flex flex-col items-center" style={{ perspective: '1200px' }}>
          <AnimatePresence mode="wait">
            <EditorCard key={editors[selected].id} profile={editors[selected]} />
          </AnimatePresence>

          <div className="mb-11 mt-12 flex items-center gap-4">
            {editors.map((editor) => (
              <EditorChip
                key={editor.id}
                editor={editor}
                selected={selected}
                onClick={() => setSelected(editor.id)}
              />
            ))}
          </div>

          <AnimatePresence mode="wait">
            <EditorSignature key={selected} selected={selected} />
          </AnimatePresence>
        </div>

        <div className="flex w-full max-w-[440px] flex-col gap-8 md:pr-6 lg:pr-0">
          <MissionStatement />
          <hr className="my-4 h-px w-full bg-gray-200" />
          <CollectionToggle apps={apps} />
        </div>
      </Container>
    </div>
  )
}

export function EditorCard({ profile }: { profile: Editor }) {
  const slug = profile.name.split(' ')[0].toLowerCase()

  return (
    <motion.div
      initial={{ rotateY: profile.id === 0 ? -90 : 90, rotate: 0 }}
      animate={{ rotateY: 0, rotate: profile.id === 0 ? 2 : -2 }}
      exit={{ rotateY: profile.id === 0 ? -90 : 90, rotate: 0 }}
      transition={easeMedium}
      style={{ backgroundImage: `url(/img/about/mission/card-background.jpg)` }}
      className={cn(
        'relative h-[480px] w-[340px] overflow-hidden rounded-3xl border border-black/10 bg-primary-500 bg-cover bg-no-repeat shadow-subtle',
        profile.name === 'Alex Bass' ? 'rotate-2' : '-rotate-2',
      )}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          style={{ backgroundImage: `url(/img/about/mission/${slug}-avatar.png)` }}
          className="absolute left-4 top-4 h-6 w-6 rounded-full border border-white/10 bg-primary-950 bg-cover bg-no-repeat"
        ></div>
        <div
          style={{ backgroundImage: `url(/img/about/mission/corner-brand.svg)` }}
          className="absolute right-4 top-4 h-6 w-6"
        ></div>

        <div className="absolute left-1/2 top-10 flex -translate-x-1/2 flex-col items-center">
          <span className="text-16 font-semibold text-white">{profile.name}</span>
          <p className="text-14 text-white/60">{profile.role}</p>
        </div>

        <Image
          src={`/img/about/mission/${slug}-headshot.png`}
          alt={profile.name}
          className="absolute bottom-0 left-0"
          width={340}
          height={480}
        />
      </motion.div>
    </motion.div>
  )
}

export function EditorChip({
  editor,
  selected,
  onClick,
}: {
  editor: Editor
  selected: number
  onClick: () => void
}) {
  const firstName = editor.name.split(' ')[0]

  return (
    <div
      className={cn(
        'relative h-8 w-fit cursor-pointer pl-1 pr-2.5 transition-colors duration-300',
        selected === editor.id ? 'text-primary-500' : 'text-black hover:text-primary-500',
      )}
      onClick={onClick}
    >
      <div className="relative z-10 flex items-center gap-1.5">
        <div
          style={{
            backgroundImage: `url(/img/about/mission/${firstName.toLowerCase()}-avatar.png)`,
          }}
          className="relative h-6 w-6 rounded-full border border-black/10 bg-primary-950 bg-cover bg-no-repeat"
        ></div>
        <span className="text-14 font-medium">{firstName}</span>
      </div>

      {selected === editor.id && (
        <motion.div
          layoutId="highlight"
          className="absolute -top-1 left-0 z-0 h-8 w-full rounded-full border border-primary-100 bg-primary-50"
        ></motion.div>
      )}
    </div>
  )
}

/**
 * Safely extracts the first name from an editor's full name for file path construction
 * @param editor - Editor object containing name and other properties
 * @returns Safe, normalized first name suitable for file paths
 */
function getEditorFirstName(editor: Editor | undefined): string {
  if (!editor || !editor.name || typeof editor.name !== 'string') {
    return 'unknown'
  }

  const trimmedName = editor.name.trim()
  if (!trimmedName) {
    return 'unknown'
  }

  // Split on any whitespace (spaces, tabs, etc.)
  const nameParts = trimmedName.split(/\s+/)
  const firstName = nameParts[0]

  if (!firstName) {
    return 'unknown'
  }

  // Normalize and encode for safe file path usage
  return encodeURIComponent(firstName.toLowerCase())
}

export function EditorSignature({ selected }: { selected: number }) {
  const editor = editors[selected]
  const firstName = getEditorFirstName(editor)
  const editorName = editor?.name || 'Unknown'
  const offset = editor?.offset || 0

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ transform: `translateY(${offset}px)` }}
      transition={easeMedium}
    >
      <Image
        src={`/img/about/mission/${firstName}-signature.svg`}
        alt={editorName}
        className="w-auto -translate-x-1.5"
        width={0}
        height={24}
      />
      <Image
        src="/img/about/mission/stamp.png"
        alt="Efficient App"
        className="absolute bottom-0 left-0 -translate-x-2/3 translate-y-2/3"
        width={72}
        height={72}
      />
    </motion.div>
  )
}

export function MissionStatement() {
  return (
    <>
      <Title
        title="For people who love great <span>software</span>."
        subtitle="On a mission"
        icon={<Icon name="gem-sparkle" />}
        type="primary"
        align="left"
      />
      <p className="leading-9 text-zinc-600">
        After 14 years of implementing software and working with teams, we kept hearing the same
        questions. There are hundreds of tools out there, but very little clarity on what’s actually
        good. Forget marketing hype and FOMO. Here you’ll find no-fluff{' '}
        <Link href="/apps" className="underlined primary">
          reviews
        </Link>
        , curated{' '}
        <Link href="/stacks" className="underlined primary">
          stacks
        </Link>
        , exclusive{' '}
        <Link href="/deals" className="underlined primary">
          deals
        </Link>
        , and more. Our promise is that we’ll never end a review with{' '}
        <i>“at the end of the day, choose what’s best for you.”</i> Here, you’ll get clear
        recommendations so you can move forward with confidence the first time.
      </p>
      <Link href="/methodology" aria-label="Our methodology">
        <Pill as="span" size="large">
          Our methodology
        </Pill>
      </Link>
    </>
  )
}

export function CollectionToggle({ apps }: AboutMissionProps) {
  const [current, setCurrent] = useState<CollectionProps>(collections[0])

  return (
    <div className="flex flex-col gap-8 text-14 text-gray-500">
      <p>
        The <span className="font-semibold text-black">6</span> efficient core platform areas
      </p>

      <div className="flex flex-col">
        <div className="relative z-10 flex gap-3 rounded-t-2xl border-x border-t border-gray-200 bg-gray-50 px-3 pb-7 pt-3">
          {collections.map((collection) => (
            <div
              key={collection.name}
              className={cn(
                'flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg border bg-white transition-colors',
                current.name === collection.name
                  ? 'border-primary-500/30 text-primary-500'
                  : 'border-gray-200 text-zinc-950 hover:border-gray-300',
              )}
              onClick={() => setCurrent(collection)}
            >
              <Icon name={collection.icon} size={16} />
            </div>
          ))}
        </div>

        <div className="relative z-20 -translate-y-4 overflow-hidden rounded-2xl border border-gray-200 text-13">
          <div className="flex flex-col gap-1.5 bg-white pb-4 pl-6 pr-10 pt-5">
            <span className="text-black">{current.name}</span>
            <p className="text-gray-500">{current.text}</p>
          </div>

          <div className="flex gap-3 border-t border-gray-200 bg-white p-4">
            {apps.slice(0, 7).map((app) => {
              const { backgroundColor } = getBrandColors(app)

              return (
                <AppIcon
                  key={app.id}
                  icon={app.icon}
                  glyph={app.glyph}
                  name={app.name}
                  slug={app.slug}
                  background={backgroundColor}
                  size={22}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
